import {
    AppPlanInfo,
    getLineItemsForPlan,
    getPlanName,
    PlanPrivacy,
    PlanType,
    SubscriptionType,
} from "common/app-plans";
import { IconImage } from "common/components/SerializedApp";
import AppPlanHeader from "./app-plan-header";
import { useCallback } from "react";
import SubscriptionToggle from "./subscription-toggle";
import { AppPlanPreviewContainer } from "./app-plan-preview-styles";
import { GlideFC } from "lib/glide-fc";
import { PromoCodeEntry } from "designer/builder/ui/smart/unified-app-plan/promo-code-entry";
import { Owner } from "common/Database";
import { ValueSlider } from "designer/builder/ui/smart/slider/value-slider";
import { ButtonVNext } from "designer/ui/smart/button-vnext/button-vnext";

interface Props {
    fromPanel: boolean;
    owner: Owner;
    appPlanInfo: AppPlanInfo;
    locked: boolean;
    plan: PlanType;
    privacy: PlanPrivacy;
    subscription: SubscriptionType;
    numPrivateUsers: number | undefined;
    appAccentColor: string;
    appIcon: IconImage;
    estimate: number | undefined;
    privateUsersChange: (numUsers: number) => void;
    subscriptionChange: (subscription: SubscriptionType) => void;
    onEditPrivateUsers: (() => void) | undefined;
    onPromoCodeApplied: () => void;
}

const AppPlanPreview: GlideFC<Props> = p => {
    const {
        fromPanel,
        owner,
        appPlanInfo,
        locked,
        plan,
        privacy,
        appAccentColor,
        appIcon,
        subscription,
        numPrivateUsers,
        estimate,
        privateUsersChange,
        subscriptionChange,
        onEditPrivateUsers,
        onPromoCodeApplied,
    } = p;
    const lineItems = getLineItemsForPlan(appPlanInfo.code, appPlanInfo.numberOfUsers, estimate);

    const privateUsersChanged = useCallback(
        (numUsers: number) => {
            if (privateUsersChange === undefined) return;
            privateUsersChange(Math.max(20, Math.floor(numUsers / 5) * 5));
        },
        [privateUsersChange]
    );

    const isAnnual = subscription === SubscriptionType.Annual;
    const isPrivate = privacy === PlanPrivacy.Private;

    const showContactSales = numPrivateUsers !== undefined && numPrivateUsers >= 250;
    const privateUserDetailClasses = !locked ? "line no-padding" : "line no-padding line-separator";
    const sliderClasses = showContactSales ? "line sm-padding" : "line line-separator";

    return (
        <AppPlanPreviewContainer locked={locked}>
            <AppPlanHeader plan={plan} privacy={privacy} appAccentColor={appAccentColor} appIcon={appIcon} />
            <div className="toggle">
                <SubscriptionToggle susbscription={subscription} subscriptionChange={subscriptionChange} />
            </div>
            <div className="info">
                <div className="title">{getPlanName(appPlanInfo.code).short} App Upgrade</div>
                {isPrivate && (
                    <>
                        <div className="line no-padding no-expand private-users-title">
                            <div className="line-title">Private Users</div>
                            {locked && !fromPanel && isAnnual && (
                                <div className="change">
                                    <ButtonVNext
                                        buttonType="minimal"
                                        variant="accent"
                                        label="Change"
                                        size="xsm"
                                        onClick={onEditPrivateUsers}
                                    />
                                </div>
                            )}
                            <div className="line-title private-users">{lineItems.privateUsers}</div>
                        </div>
                        <div className={privateUserDetailClasses}>
                            <div className="line-title-sm light">per user per month</div>
                            <div className="line-title-sm private-users">x {lineItems.costPerUser}</div>
                        </div>
                        {!locked && isAnnual && (
                            <div className={sliderClasses}>
                                <ValueSlider
                                    min={0}
                                    max={250}
                                    clampedMin={20}
                                    step={1}
                                    value={numPrivateUsers ?? 20}
                                    margin={0}
                                    onSelectionChanged={privateUsersChanged}
                                />
                            </div>
                        )}
                    </>
                )}
                {showContactSales && (
                    <>
                        <div className="line sm-padding">
                            <div className="line-title-sm light">Bulk prices offered over 250 private users</div>
                        </div>
                        <div className="line line-separator contact-sales-button">
                            <ButtonVNext
                                buttonType="secondary"
                                size="md"
                                variant="default"
                                label="Contact Sales"
                                href="mailto:sales@glideapps.com"
                            />
                        </div>
                    </>
                )}
                <div className="line line-separator">
                    <div className="line-title light">{getPlanName(appPlanInfo.code).short} Plan</div>
                    <div>{lineItems.planBaseCost}</div>
                </div>
                {isAnnual && (
                    <div className="line line-separator">
                        <div className="line-title light">Sub-Total</div>
                        <div>{lineItems.subTotal}</div>
                    </div>
                )}
                <div className="line line-last">
                    <div className="line-title">Due Today</div>
                    <div className="line-total">{lineItems.dueToday}</div>
                </div>
            </div>
            <div className="promo-container">
                <PromoCodeEntry owner={owner} subscription={subscription} onPromoCodeApplied={onPromoCodeApplied} />
            </div>
        </AppPlanPreviewContainer>
    );
};

export default AppPlanPreview;
